import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Spin, Statistic, Progress, Avatar, Space, Divider } from 'antd';
import { Line, Column } from '@ant-design/plots';
import { GET } from '../utils/apiCalls';
import API from '../config/api';
import moment from 'moment';
import { 
  ShopOutlined, 
  AppstoreOutlined, 
  DollarOutlined, 
  ShoppingCartOutlined,
  RiseOutlined,
  UserOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';

const { Title, Text } = Typography;

const HomeScreen = () => {
  const [homeData, setHomeData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useSelector((state: any) => state.User);

  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchHomeData = async () => {
    setLoading(true);
    try {
      let url = API.HOME_DATA;
      if(!user.staff?.staffAccess?.includes("administrator")){
        url = API.HOME_DATA + `?branchId=${user?.staff?.companyid}`;
      }
      const response: any = await GET(url, null);
      setHomeData(response.data);
    } catch (error) {
      setHomeData(null);
    }
    setLoading(false);
  };

  // Chart config
  const lineConfig = {
    data: homeData?.chart?.map((item: any) => ({ ...item, sales: item.value })) || [],
    xField: 'date',
    yField: 'sales',
    smooth: true,
    area: { style: { fill: 'l(270) 0:#1890ff 1:#52c41a', fillOpacity: 0.1 } },
    lineStyle: { stroke: '#1890ff', lineWidth: 3 },
    xAxis: { 
      label: { style: { fill: '#666' } },
      line: { style: { stroke: '#e8e8e8' } }
    },
    yAxis: { 
      label: { style: { fill: '#666' } },
      line: { style: { stroke: '#e8e8e8' } }
    },
    tooltip: { 
      showMarkers: false,
      domStyles: {
        'g2-tooltip': {
          background: 'rgba(255, 255, 255, 0.95)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          borderRadius: '6px',
          border: '1px solid #e8e8e8'
        }
      }
    },
    animation: { appear: { animation: 'path-in', duration: 1000 } },
  };

  // Mock data for additional charts
  const weeklyData = [
    { day: 'Mon', sales: 1200, orders: 45 },
    { day: 'Tue', sales: 1800, orders: 52 },
    { day: 'Wed', sales: 1500, orders: 48 },
    { day: 'Thu', sales: 2200, orders: 65 },
    { day: 'Fri', sales: 2800, orders: 78 },
    { day: 'Sat', sales: 3200, orders: 89 },
    { day: 'Sun', sales: 2600, orders: 72 },
  ];

  const columnConfig = {
    data: weeklyData,
    xField: 'day',
    yField: 'orders',
    color: '#52c41a',
    columnStyle: { radius: [4, 4, 0, 0] },
    xAxis: { 
      label: { style: { fill: '#666' } },
      line: { style: { stroke: '#e8e8e8' } }
    },
    yAxis: { 
      label: { style: { fill: '#666' } },
      line: { style: { stroke: '#e8e8e8' } }
    },
    tooltip: { 
      domStyles: {
        'g2-tooltip': {
          background: 'rgba(255, 255, 255, 0.95)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          borderRadius: '6px',
          border: '1px solid #e8e8e8'
        }
      }
    },
  };

  if (loading) {
    return (
      <div style={{ 
        width: '100%', 
        padding: '24px', 
        background: '#f5f5f5', 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      padding: '24px', 
      paddingLeft: '20px', 
      background: '#f5f5f5', 
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: 15 }}>
        <Title level={2} style={{ margin: 0, color: '#262626' }}>
          Dashboard Overview
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Welcome back! Here's what's happening with your business today.
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card 
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: 'none'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <Statistic
              title="Today's Sales"
              value={Number(homeData?.today_amount || 0).toFixed(2)}
              prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a', fontSize: '28px', fontWeight: 'bold' }}
              suffix="INR"
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 14 }}>
                <ArrowUpOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                12% increase from yesterday
              </Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card 
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: 'none'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <Statistic
              title="Total Orders"
              value={homeData?.today_order || 0}
              prefix={<ShoppingCartOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff', fontSize: '28px', fontWeight: 'bold' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 14 }}>
                <ArrowUpOutlined style={{ color: '#1890ff', marginRight: 4 }} />
                8% increase from yesterday
              </Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card 
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: 'none'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <Statistic
              title="Active Users"
              value={245}
              prefix={<UserOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1', fontSize: '28px', fontWeight: 'bold' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 14 }}>
                <ArrowUpOutlined style={{ color: '#722ed1', marginRight: 4 }} />
                15% increase from yesterday
              </Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card 
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: 'none'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <Statistic
              title="Conversion Rate"
              value={68.2}
              prefix={<RiseOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16', fontSize: '28px', fontWeight: 'bold' }}
              suffix="%"
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 14 }}>
                <ArrowDownOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
                3% decrease from yesterday
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {/* Sales Chart */}
        <Col xs={24} lg={16}>
          <Card 
            title={
              <Space>
                <RiseOutlined style={{ color: '#1890ff' }} />
                <span>Sales Overview</span>
              </Space>
            }
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: 'none'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            {homeData?.chart?.length ? (
              <Line {...lineConfig} />
            ) : (
              <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Text type="secondary">No chart data available</Text>
              </div>
            )}
          </Card>
        </Col>

        {/* Orders Chart */}
        <Col xs={24} lg={8}>
          <Card 
            title={
              <Space>
                <ShoppingCartOutlined style={{ color: '#52c41a' }} />
                <span>Weekly Orders</span>
              </Space>
            }
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: 'none'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            <Column {...columnConfig} />
          </Card>
        </Col>
      </Row>

      {/* Top Performers Row */}
      <Row gutter={[24, 24]}>
        {/* Top Branch */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <ShopOutlined style={{ color: '#52c41a' }} />
                <span>{ user?.staff?.staffAccess?.includes("administrator") ? "Top Performing Branch" : "Branch Performance" }</span>
              </Space>
            }
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: 'none'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            {homeData?.today_branch?.CompanyMaster ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  <Avatar 
                    size={64} 
                    icon={<ShopOutlined />} 
                    style={{ backgroundColor: '#52c41a', marginRight: 16 }}
                  />
                  <div>
                    <Title level={4} style={{ margin: 0, color: '#262626' }}>
                      {homeData.today_branch.CompanyMaster.bname}
                    </Title>
                    <Text type="secondary">Branch Manager</Text>
                  </div>
                </div>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="Total Sales"
                      value={Number(homeData.today_branch.totalSales).toFixed(2)}
                      prefix="₹"
                      valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Orders"
                      value={homeData.today_branch.totalOrders}
                      valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                    />
                  </Col>
                </Row>
                
                <Divider />
                
                <div>
                  <Text type="secondary">Performance</Text>
                  <Progress 
                    percent={85} 
                    strokeColor="#52c41a" 
                    showInfo={false}
                    style={{ marginTop: 8 }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {moment().format('lll')}
                  </Text>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">No branch data available</Text>
              </div>
            )}
          </Card>
        </Col>

        {/* Top Product */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <AppstoreOutlined style={{ color: '#1890ff' }} />
                <span>Best Selling Product</span>
              </Space>
            }
            style={{ 
              borderRadius: 12, 
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: 'none'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            {homeData?.today_product?.productMaster ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  <Avatar 
                    size={64} 
                    icon={<AppstoreOutlined />} 
                    style={{ backgroundColor: '#1890ff', marginRight: 16 }}
                  />
                  <div>
                    <Title level={4} style={{ margin: 0, color: '#262626' }}>
                      {homeData.today_product.productMaster.idescription}
                    </Title>
                    <Text type="secondary">Product ID: {homeData.today_product.productMaster.id}</Text>
                  </div>
                </div>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="Revenue"
                      value={Number(homeData.today_product.totalSold * homeData.today_product.productMaster.sp_price).toFixed(2)}
                      prefix="₹"
                      valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Units Sold"
                      value={homeData.today_product.totalSold}
                      valueStyle={{ color: '#52c41a', fontSize: '20px' }}
                    />
                  </Col>
                </Row>
                
                <Divider />
                
                <div>
                  <Text type="secondary">Sales Progress</Text>
                  <Progress 
                    percent={92} 
                    strokeColor="#1890ff" 
                    showInfo={false}
                    style={{ marginTop: 8 }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {moment().format('lll')}
                  </Text>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">No product data available</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default HomeScreen; 